import './RadioGroup.css'
import cx from 'classnames'
import { Label } from '../InputLabel/InputLabel'

// Shared label classes for radio and checkbox components
export const radioCheckboxClasses = {
  label: 'radio-checkbox-label'
}

const directionClasses = {
  horizontal: 'radio-group-horizontal',
  vertical: 'radio-group-vertical'
}

export function Radio<T extends string | number | boolean>({
  id,
  value,
  name,
  isChecked,
  onChange,
  disabled
}: {
  id?: string
  value: T
  isChecked: boolean
  name: string
  onChange?: (value: T) => void
  disabled?: boolean
}) {
  return (
    <div className="size-6 flex justify-center items-center">
      <input
        type="radio"
        id={id}
        disabled={disabled}
        value={value.toString()}
        name={name}
        checked={isChecked}
        onChange={onChange ? () => onChange(value) : undefined}
        className={cx(
          'radio-base',
          'radio-checked',
          'radio-checked-inner',
          'radio-disabled',
          'radio-disabled-check'
        )}
      />
    </div>
  )
}

export function RadioButton<T extends string | number | boolean>({
  id,
  name,
  value,
  isChecked,
  onChange,
  label,
  disabled
}: {
  id: string
  value: T
  isChecked: boolean
  name: string
  onChange: (value: T) => void
  label: string
  disabled?: boolean
}) {
  return (
    <div className="cursor-pointer flex relative items-center">
      <Radio
        id={id}
        value={value}
        name={name}
        isChecked={isChecked}
        onChange={onChange}
        disabled={disabled}
      />
      {label && (
        <label
          htmlFor={id}
          className={cx(
            'vertical-align-baseline',
            radioCheckboxClasses.label,
            disabled ? 'pointer-events-none' : ''
          )}
        >
          {label}
        </label>
      )}
    </div>
  )
}

export function RadioGroup<T extends string | number | boolean>({
  options,
  name,
  value,
  label,
  onChange,
  direction = 'horizontal',
  disabled
}: {
  direction?: 'horizontal' | 'vertical'
  label?: string
  value: T
  options: { label: string; value: T; callout?: string; disabled?: boolean }[]
  onChange: (value: T) => void
  name: string
  disabled?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className={cx(directionClasses[direction])}>
        {options.map(option => (
          <RadioButton
            key={option.value.toString()}
            id={`${name}:${option.value.toString()}`}
            value={option.value}
            isChecked={value === option.value}
            label={option.label}
            name={name}
            onChange={onChange}
            disabled={option.disabled || disabled}
          />
        ))}
      </div>
    </div>
  )
}
